import React, { useEffect } from 'react';
import { EQUIPMENT_MODELS, EQUIPMENT_TYPES, PRIORITY_LEVELS } from '../constants';

const EquipmentSection = ({ 
  formData, 
  handleChange, 
  isSubmitting 
}) => {
  // Get available models based on selected equipment type
  const getAvailableModels = () => {
    if (!formData.typeOfEquipment || formData.typeOfEquipment === EQUIPMENT_TYPES.OTHERS) {
      return [];
    }
    return EQUIPMENT_MODELS[formData.typeOfEquipment] || [];
  };

  // Handle equipment type change - clear model when type changes
  const handleEquipmentTypeChange = (event) => {
    const newType = event.target.value;
    
    // Call the original handleChange
    handleChange(event);
    
    // Clear model when type changes (except when selecting "Others")
    if (newType !== formData.typeOfEquipment) {
      const modelEvent = {
        target: {
          name: 'modelOfEquipment',
          value: ''
        }
      };
      handleChange(modelEvent);
      
      // Also clear custom model if it exists
      if (formData.customModel) {
        const customModelEvent = {
          target: {
            name: 'customModel',
            value: ''
          }
        };
        handleChange(customModelEvent);
      }
    }
  };

  const availableModels = getAvailableModels();

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-800 font-poppins mb-4 border-b-2 border-gray-200 pb-2">
        Equipment Information
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Date of Request */}
        <div className="relative">
          <label htmlFor="dateOfRequest" className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
            Date of Request *
          </label>
          <input
            type="date"
            id="dateOfRequest"
            name="dateOfRequest"
            required
            value={formData.dateOfRequest}
            onChange={handleChange}
            disabled={isSubmitting}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg font-poppins transition-all duration-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          />
        </div>

        {/* Equipment Type */}
        <div className="relative">
          <label htmlFor="typeOfEquipment" className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
            Type of Equipment *
          </label>
          <select
            id="typeOfEquipment"
            name="typeOfEquipment"
            required
            value={formData.typeOfEquipment}
            onChange={handleEquipmentTypeChange}
            disabled={isSubmitting}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg font-poppins transition-all duration-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            <option value="">Select Type</option>
            <option value={EQUIPMENT_TYPES.DESKTOP}>Desktop</option>
            <option value={EQUIPMENT_TYPES.LAPTOP}>Laptop</option>
            <option value={EQUIPMENT_TYPES.PRINTER}>Printer</option>
            <option value={EQUIPMENT_TYPES.SCANNER}>Scanner</option>
            <option value={EQUIPMENT_TYPES.OTHERS}>Others</option>
          </select>
        </div>

        {/* Model Selection - Only show when equipment type is selected */}
        {formData.typeOfEquipment && (
          <div className="relative">
            {formData.typeOfEquipment === EQUIPMENT_TYPES.OTHERS ? (
              <>
                <label htmlFor="modelOfEquipment" className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
                  Equipment Model *
                </label>
                <input
                  type="text"
                  id="modelOfEquipment"
                  name="modelOfEquipment"
                  required
                  value={formData.modelOfEquipment || ''}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg font-poppins transition-all duration-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  placeholder="Enter model"
                />
              </>
            ) : (
              <>
                <label htmlFor="modelOfEquipment" className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
                  Model of Equipment *
                </label>
                <select
                  id="modelOfEquipment"
                  name="modelOfEquipment"
                  required
                  value={formData.modelOfEquipment}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg font-poppins transition-all duration-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  <option value="">Select Model</option>
                  {availableModels.map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>
        )}

        {/* Priority */}
        <div className="relative">
          <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
            Priority *
          </label>
          <select
            id="priority"
            name="priority"
            required
            value={formData.priority}
            onChange={handleChange}
            disabled={isSubmitting}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg font-poppins transition-all duration-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            <option value="">Select Priority</option>
            <option value={PRIORITY_LEVELS.HIGH}>High</option>
            <option value={PRIORITY_LEVELS.MEDIUM}>Medium</option>
            <option value={PRIORITY_LEVELS.LOW}>Low</option>
          </select>
        </div>
      </div>

      {/* Second Row - Serial Number and Custom Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        {/* Serial Number */}
        <div className="relative">
          <label htmlFor="serialNo" className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
            Serial No. *
          </label>
          <input
            type="text"
            id="serialNo"
            name="serialNo"
            required
            value={formData.serialNo}
            onChange={handleChange}
            disabled={isSubmitting}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg font-poppins transition-all duration-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            placeholder="Enter equipment serial number"
          />
        </div>

        {/* Custom Equipment Type */}
        {formData.typeOfEquipment === EQUIPMENT_TYPES.OTHERS && (
          <div className="relative">
            <label htmlFor="customEquipmentType" className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
              Specify Equipment Type *
            </label>
            <input
              type="text"
              id="customEquipmentType"
              name="customEquipmentType"
              required
              value={formData.customEquipmentType || ''}
              onChange={handleChange}
              disabled={isSubmitting}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg font-poppins transition-all duration-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              placeholder="Enter equipment type"
            />
          </div>
        )}

        {/* Custom Model Input - Only show when "Other" model is selected */}
        {formData.modelOfEquipment === 'Other' && formData.typeOfEquipment !== EQUIPMENT_TYPES.OTHERS && (
          <div className="relative">
            <label htmlFor="customModel" className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
              Specify Other Model *
            </label>
            <input
              type="text"
              id="customModel"
              name="customModel"
              required
              value={formData.customModel || ''}
              onChange={handleChange}
              disabled={isSubmitting}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg font-poppins transition-all duration-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              placeholder="Enter the specific model"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EquipmentSection;